/**
 * a11y.js — Accessibility helpers for keyboard-equivalent activation.
 *
 * Phase 11 slot 7 (2026-04-24): WCAG AA compliance pass.
 *
 * Use these helpers whenever a non-button element (div, span, li, etc.)
 * needs to be clickable. The browser's default keyboard activation
 * (Enter / Space) only works for native interactive elements. Adding
 * `role="button"` + `tabIndex={0}` makes the element discoverable by
 * keyboard + AT users; we still need to wire the keyboard handler
 * explicitly because role="button" alone doesn't trigger onClick on
 * Enter or Space.
 *
 * Pattern (preferred):
 *
 *   import { activateOnEnterOrSpace } from '../utils/a11y';
 *
 *   <div
 *     role="button"
 *     tabIndex={0}
 *     onClick={handleClick}
 *     onKeyDown={activateOnEnterOrSpace(handleClick)}
 *     aria-label="Open project"   // strongly recommended for non-text triggers
 *   >
 *     ...
 *   </div>
 *
 * Why we keep the div instead of converting to a <button>:
 *   - <button> normalizes margins / padding / display. A wholesale swap
 *     across the codebase would touch CSS / layout for every clickable
 *     card. Keeping the div + role attribute preserves layout while
 *     fixing the a11y bug.
 *   - For NEW components, prefer a real <button> and avoid this helper.
 */

/**
 * Returns a keyboard handler that calls `handler` when the user presses
 * Enter or Space. Use as the `onKeyDown` companion to a click handler on
 * a non-button element.
 *
 * @param {(e: KeyboardEvent) => void} handler - the click handler to mirror
 * @returns {(e: KeyboardEvent) => void}
 *
 * @example
 *   <div
 *     role="button"
 *     tabIndex={0}
 *     onClick={onSelect}
 *     onKeyDown={activateOnEnterOrSpace(onSelect)}
 *   >...</div>
 */
export function activateOnEnterOrSpace(handler) {
  return (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      handler(event);
    }
  };
}

/**
 * Returns a keyboard handler that calls `handler` ONLY on Enter (not Space).
 * Use for elements where Space should preserve its native scrolling / form
 * meaning — typically form-row toggles or list-item activation that lives
 * inside a scrollable region.
 *
 * @param {(e: KeyboardEvent) => void} handler
 * @returns {(e: KeyboardEvent) => void}
 */
export function activateOnEnter(handler) {
  return (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handler(event);
    }
  };
}

/**
 * Common a11y prop bundle for clickable non-button elements. Spreads onto
 * the element to apply role, tabIndex, and the keyboard handler in one go.
 *
 * @param {(e: Event) => void} handler - click handler (also wired to Enter/Space)
 * @param {Object} [opts]
 * @param {string} [opts.label] - aria-label override (recommended for icon-only buttons)
 * @returns {Object} prop bundle
 *
 * @example
 *   <div onClick={onSelect} {...clickableProps(onSelect, { label: 'Open' })}>
 *     ...
 *   </div>
 *
 * Note: keep the `onClick` on the element itself for click-to-activate
 * (the bundle's onKeyDown only handles keyboard). Spreading after
 * `onClick={...}` on the JSX preserves your click handler since the bundle
 * doesn't include one.
 */
export function clickableProps(handler, opts = {}) {
  const props = {
    role: 'button',
    tabIndex: 0,
    onKeyDown: activateOnEnterOrSpace(handler),
  };
  if (opts.label) props['aria-label'] = opts.label;
  return props;
}

/**
 * Visually-hidden CSS for screen-reader-only content. Mirrors Bootstrap's
 * `.sr-only` / Tailwind's `.sr-only` utility. Use when an element must be
 * announced to AT but not seen.
 *
 * @example
 *   <span style={visuallyHidden}>Loading projects, please wait</span>
 */
export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
